import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, Switch, InputNumber, Alert, Divider, Tag, message, Row, Col } from 'antd';
import { MailOutlined, CheckCircleOutlined } from '@ant-design/icons';

const { Option } = Select;

interface EmailSettingsProps { data?: any; onRefresh: () => void; }

const EmailSettings: React.FC<EmailSettingsProps> = ({ onRefresh }) => {
  const [form] = Form.useForm();
  const [provider, setProvider] = useState('brevo');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const tenantId = localStorage.getItem('hr_tenant_id') || 'superadmin';

  useEffect(() => {
    fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/settings/email_settings.php?tenant_id=${tenantId}`)
      .then(r => r.json()).then(d => {
        if (d.success && d.data) {
          setProvider(d.data.email_service_provider || 'brevo');
          form.setFieldsValue(d.data);
        }
      }).catch(() => {});
  }, []);

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      const r = await fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/settings/email_settings.php?tenant_id=${tenantId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...values, tenant_id: tenantId })
      });
      const d = await r.json();
      if (d.success) { message.success('Email settings saved successfully'); onRefresh(); }
      else message.error(d.error || 'Failed to save');
    } finally { setLoading(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/settings/email_settings.php?tenant_id=${tenantId}&action=test`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenant_id: tenantId })
      });
      const d = await r.json();
      setTestResult(d);
      if (d.success) message.success('Test email sent successfully!');
      else message.error(d.error || 'Test failed');
    } finally { setTesting(false); }
  };

  return (
    <div>
      <Alert message="Configure your email provider to send notifications to employees. You can use your own Brevo, SendGrid, Mailgun, or SMTP account." type="info" showIcon style={{ marginBottom: 16 }} />
      <Card title="Email Provider Configuration">
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="email_service_provider" label="Email Provider" rules={[{ required: true }]}>
                <Select onChange={v => setProvider(v)}>
                  <Option value="brevo"><Tag color="blue">Brevo</Tag> Brevo (Sendinblue) — Recommended</Option>
                  <Option value="sendgrid"><Tag color="green">SendGrid</Tag> SendGrid</Option>
                  <Option value="mailgun"><Tag color="orange">Mailgun</Tag> Mailgun</Option>
                  <Option value="smtp"><Tag color="purple">SMTP</Tag> Custom SMTP Server</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="email_enabled" label="Email Notifications" valuePropName="checked">
                <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="sender_email" label="Sender Email" rules={[{ required: true, type: 'email' }]}>
                <Input prefix={<MailOutlined />} placeholder="hr@yourcompany.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sender_name" label="Sender Name">
                <Input placeholder="Your Company HR" />
              </Form.Item>
            </Col>
          </Row>

          {(provider === 'brevo' || provider === 'sendgrid' || provider === 'mailgun') && (
            <Form.Item name="api_key" label={`${provider.charAt(0).toUpperCase()+provider.slice(1)} API Key`} rules={[{ required: true }]}>
              <Input.Password placeholder="Enter your API key" />
            </Form.Item>
          )}

          {provider === 'smtp' && (
            <>
              <Row gutter={16}>
                <Col span={16}>
                  <Form.Item name="smtp_host" label="SMTP Host" rules={[{ required: true }]}>
                    <Input placeholder="smtp.gmail.com" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="smtp_port" label="Port">
                    <InputNumber style={{ width: '100%' }} placeholder="587" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="smtp_username" label="Username">
                    <Input placeholder="your@email.com" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="smtp_password" label="Password">
                    <Input.Password placeholder="SMTP password or app password" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="smtp_encryption" label="Encryption">
                <Select>
                  <Option value="tls">TLS (Recommended)</Option>
                  <Option value="ssl">SSL</Option>
                  <Option value="none">None</Option>
                </Select>
              </Form.Item>
            </>
          )}

          <Divider />
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="daily_email_limit" label="Daily Email Limit">
                <InputNumber style={{ width: '100%' }} min={1} max={10000} placeholder="300" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="reply_to_email" label="Reply-To Email">
                <Input placeholder="replies@yourcompany.com" />
              </Form.Item>
            </Col>
          </Row>

          {testResult && (
            <Alert type={testResult.success ? 'success' : 'error'}
              message={testResult.success ? '✅ Test email sent successfully' : '❌ Test failed'}
              description={testResult.error || testResult.message}
              showIcon style={{ marginBottom: 16 }} />
          )}

          <Row gutter={8}>
            <Col><Button type="primary" htmlType="submit" loading={loading} icon={<CheckCircleOutlined />}>Save Settings</Button></Col>
            <Col><Button onClick={handleTest} loading={testing}>Send Test Email</Button></Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default EmailSettings;
